import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import {
  ArrowTopRightOnSquareIcon,
  Cog6ToothIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { TableToolbar } from "../../../../components/utils/TableToolbar";
import { useTableView } from "../../../../components/utils/PMTableView/useTableView";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../../components/utils/PMTableView/RightDrawer";
import { dateToString, updateUrlWithException } from "../../../../components/Utils";
import { getDeviationsAsync } from "../../../../slices/quality/deviation";
import DeviationNewDialog from "../../../../components/dashboard/quality/deviations/deviationNewDialog";
import {
  DEVIATION_LIST_FIELDS,
  DeviationsDataTable,
  getDeviationColumns,
} from "../../../../components/dashboard/quality/deviations/deviationsTableNewView";
import {
  DeviationPriority,
  DeviationSeverity,
  DeviationStatus,
  deviationEmailDeliveryFailed,
  deviationStatusOptions,
  getDeviationTypeOptions,
  priorityOptions,
} from "../../../../components/dashboard/quality/deviations/utils";
import { fetchUsers } from "../../../../api/user";
import { getDeviationPdf } from "../../../../api/quality/deviation";
import { HseRecordActionsMenu } from "../../../../components/dashboard/hse/HseRecordActionsMenu";

export const DeviationsList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  const isAdmin = currentUser?.roles?.some((role) => role.name === "admin");

  const [searchParams] = useSearchParams();

  const skipFetchRef = useRef(false);
  // Prevents double-dispatch when handleLoadMore updates controller.page
  const skipNextDispatchRef = useRef(false);

  const getInitialFiltersFromUrl = () => {
    const filtersParam = searchParams.get("filters");
    if (filtersParam) {
      try {
        return JSON.parse(filtersParam);
      } catch (e) {
        console.error("Error parsing filters from URL:", e);
      }
    }
    return [{ name: "status", value: JSON.stringify(["1", "2"]) }];
  };

  const [drawerDeviation, setDrawerDeviation] = useState(null);

  const [controller, setController] = useState({
    filters: getInitialFiltersFromUrl(),
    page: searchParams.get("page") ? parseInt(searchParams.get("page"), 10) : 1,
    per_page: searchParams.get("per_page")
      ? parseInt(searchParams.get("per_page"), 10)
      : 50,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "desc",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "created_at",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    fields: DEVIATION_LIST_FIELDS,
  });

  const itemState = useSelector((store) => store.deviations);
  const { isLoading, totalCount, emailFailedCount } = itemState;
  const emailFailedOnPage = (itemState.data || []).some((row) =>
    deviationEmailDeliveryFailed(row)
  );

  const [userOptions, setUserOptions] = useState([]);
  const [deviationTypeOptions, setDeviationTypeOptions] = useState([]);

  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
        filters:
          controller.filters && controller.filters.length > 0
            ? JSON.stringify(controller.filters)
            : null,
      },
      "filters"
    );
  }, [controller]);

  async function fetchAndMapUsers() {
    const usersController = {
      filters: [{ name: "role", value: "deviations" }],
      page: 1,
      per_page: 100,
      query: "",
      sort: "asc",
      sortBy: "name",
      view: "all",
    };

    await fetchUsers(usersController)
      .then((res) => {
        if (res.data) {
          const array = [];
          res.data.forEach((row) => {
            array.push({
              value: row.id?.toString(),
              label: row.displayTitle || row.name,
            });
          });
          setUserOptions(array);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }

  useEffect(() => {
    fetchAndMapUsers();
    getDeviationTypeOptions().then((options) => {
      setDeviationTypeOptions(
        (options || []).map((option) => ({
          value: String(option.value),
          label: option.title,
        }))
      );
    });
  }, []);

  const handleExportDeviationPdf = async (deviation) => {
    if (!deviation?.id) return;
    try {
      const { blob, filename } = await getDeviationPdf({ id: deviation.id });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "avvik.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("hse.actions.export_pdf_success"));
    } catch (err) {
      console.error(err);
      toast.error(err?.message || t("hse.actions.export_pdf_error"));
    }
  };

  // Column definitions + table-view state
  const columns = useMemo(() => {
    const base = getDeviationColumns(t);
    return [
      ...base,
      {
        key: "actions",
        label: t("hse.actions.menu"),
        width: 56,
        always: true,
        stopRowClick: true,
        cell: (item) => (
          <HseRecordActionsMenu
            items={[
              {
                key: "open",
                label: t("hse.actions.open"),
                icon: EyeIcon,
                onClick: () => navigate(`/dashboard/hse-ks/deviations/${item.id}`),
              },
              {
                key: "edit",
                label: t("hse.actions.edit"),
                icon: PencilIcon,
                hidden: !item?.id,
                onClick: () => navigate(`/dashboard/hse-ks/deviations/${item.id}/edit`),
              },
              {
                key: "export_pdf",
                label: t("hse.actions.export_pdf"),
                icon: DocumentArrowDownIcon,
                hidden: !item?.id,
                onClick: () => handleExportDeviationPdf(item),
              },
            ]}
          />
        ),
      },
    ];
  }, [t, navigate]);
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: "deviations",
    columns: columnMeta,
    getSnapshot: () => ({
      filters: controller.filters,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
    }),
    applySnapshot: (snap) => {
      skipFetchRef.current = false;
      setController((c) => ({
        ...c,
        page: 1,
        filters: snap.filters || [],
        query: snap.query || "",
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
      }));
    },
  });

  const tableSort = { field: controller.sortBy, dir: controller.sort };
  const handleSort = (field) => {
    skipFetchRef.current = false;
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort: c.sortBy === field && c.sort === "asc" ? "desc" : "asc",
    }));
  };

  const handleFiltersChange = (filters) => {
    const backendFilters = [];

    if (filters.deviationStatuses?.length > 0) {
      backendFilters.push({
        name: "status",
        value: JSON.stringify(filters.deviationStatuses),
      });
    }

    if (filters.users?.length > 0) {
      backendFilters.push({
        name: "responsible_user",
        value: JSON.stringify(filters.users),
      });
    }

    if (filters.deviationTypes?.length > 0) {
      backendFilters.push({
        name: "deviation_type",
        value: JSON.stringify(filters.deviationTypes),
      });
    }

    if (filters.priorities?.length > 0) {
      backendFilters.push({
        name: "priority",
        value: JSON.stringify(filters.priorities),
      });
    }

    if (filters.onlyLinkedWorkOrders) {
      backendFilters.push({
        name: "work_order",
        value: true,
      });
    }

    skipFetchRef.current = false;
    setController((c) => ({
      ...c,
      page: 1,
      query: filters.query || "",
      filters: backendFilters,
    }));
  };

  const getInitialFilters = () => {
    const initialFilters = {
      query: controller.query || "",
      deviationStatuses: [],
      users: [],
      deviationTypes: [],
      priorities: [],
      onlyLinkedWorkOrders: false,
    };

    if (controller.filters?.length > 0) {
      controller.filters.forEach((filter) => {
        try {
          const values = JSON.parse(filter.value);
          switch (filter.name) {
            case "status":
              initialFilters.deviationStatuses = Array.isArray(values)
                ? values.map(String)
                : [String(values)];
              break;
            case "responsible_user":
              initialFilters.users = Array.isArray(values) ? values : [values];
              break;
            case "deviation_type":
              initialFilters.deviationTypes = Array.isArray(values)
                ? values.map(String)
                : [String(values)];
              break;
            case "priority":
              initialFilters.priorities = Array.isArray(values)
                ? values.map(String)
                : [String(values)];
              break;
            case "work_order":
              initialFilters.onlyLinkedWorkOrders = true;
              break;
          }
        } catch (error) {
          if (filter.name === "work_order") {
            initialFilters.onlyLinkedWorkOrders = true;
          }
        }
      });
    } else {
      initialFilters.deviationStatuses = ["1", "2"];
    }

    return initialFilters;
  };

  // Infinite scroll
  const handleLoadMore = () => {
    const nextPage = (controller.page || 1) + 1;
    skipNextDispatchRef.current = true;
    dispatch(getDeviationsAsync({ ...controller, page: nextPage, append: true }));
    setController((c) => ({ ...c, page: nextPage }));
  };

  const debouncedDispatchAction = debounce((ctrl) => {
    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      return;
    }
    dispatch(getDeviationsAsync(ctrl));
  }, 250);

  useEffect(() => {
    if (skipNextDispatchRef.current) {
      skipNextDispatchRef.current = false;
      return;
    }
    debouncedDispatchAction(controller);
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  const [deviationNewDialogOpen, setDeviationNewDialogOpen] = useState(false);

  const listCount = totalCount != null ? `${totalCount}` : null;

  return (
    <>
      <DashboardHeader
        variant="list"
        subtitle=""
        actions={[
          {
            label: t("deviation.settings.title"),
            icon: Cog6ToothIcon,
            path: "/dashboard/hse-ks/deviations/settings",
            hidden: !isAdmin,
            menu: false,
          },
        ]}
        title={t("deviation.list.title")}
        listCount={listCount}
        mainButton={{
          label: t("common.new"),
          action: () => setDeviationNewDialogOpen(true),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="p-5 space-y-4">
          {/* Filter card */}
          <ModuleFilter
            filterCard
            disabled={isLoading}
            onFiltersChange={handleFiltersChange}
            initialFilters={getInitialFilters()}
            searchPlaceholder="Søk i avvik"
            filterTitle="Filtrer avvik"
            hideStatusFilter={true}
            userOptions={userOptions}
            customDropdownFilters={[
              {
                key: "deviationStatuses",
                label: t("deviation.list.statuses"),
                options: deviationStatusOptions.map((status) => ({
                  value: String(status.value),
                  label: status.label,
                })),
              },
              {
                key: "deviationTypes",
                label: t("deviation.list.types"),
                options: deviationTypeOptions,
              },
              {
                key: "priorities",
                label: t("deviation.list.priorities"),
                options: priorityOptions.map((priority) => ({
                  value: String(priority.value),
                  label: priority.label,
                })),
              },
            ]}
            customCheckboxButtonFilters={[
              {
                key: "onlyLinkedWorkOrders",
                label: "Kun knyttet arbeidsordre",
                defaultValue: false,
              },
            ]}
          />

          {(emailFailedCount > 0 || emailFailedOnPage) && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-3 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-900"
            >
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
              <p>{t("deviation.email_failed.banner")}</p>
            </div>
          )}

          {/* Table card */}
          <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)]">
            <TableToolbar
              groupOptions={groupOptions}
              activeGroup={tableView.groupBy}
              onGroupChange={tableView.setGroupBy}
              columns={columnMeta}
              columnOrder={tableView.order}
              visibleKeys={tableView.visibleKeys}
              onToggleColumn={tableView.toggleColumn}
              onReorderColumn={tableView.reorderColumn}
              onResetColumns={tableView.resetColumns}
              savedViews={tableView.views}
              activeSavedViewId={tableView.activeViewId}
              savedViewDirty={tableView.dirty}
              onApplySavedView={tableView.applyView}
              onSaveAsView={tableView.saveAsView}
              onUpdateView={tableView.updateView}
              onDeleteView={tableView.deleteView}
              density={tableView.density}
              onDensityChange={tableView.setDensity}
            />
            <DeviationsDataTable
              columns={columns}
              visibleKeys={tableView.visibleKeys}
              widths={tableView.widths}
              onResize={tableView.resizeColumn}
              groupBy={tableView.groupBy}
              sort={tableSort}
              onSort={handleSort}
              itemState={itemState}
              density={tableView.density}
              hasMore={(totalCount || 0) > (itemState?.data?.length || 0)}
              onLoadMore={handleLoadMore}
              onRowClick={(deviation) => setDrawerDeviation(deviation)}
            />
          </div>
        </div>
      </DashboardHeader>

      {/* Row detail pull-out drawer */}
      <RightDrawer
        open={!!drawerDeviation}
        onClose={() => setDrawerDeviation(null)}
        eyebrow={
          drawerDeviation
            ? `${t("deviation.table.number")} ${drawerDeviation.number || ""}`
            : ""
        }
        title={
          drawerDeviation?.title ||
          drawerDeviation?.displayTitle ||
          t("deviation.table.deviation")
        }
        actions={[
          {
            label: t("hse.actions.open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () =>
              navigate(`/dashboard/hse-ks/deviations/${drawerDeviation.id}`),
          },
          {
            label: t("hse.actions.edit"),
            icon: PencilIcon,
            onClick: () =>
              navigate(`/dashboard/hse-ks/deviations/${drawerDeviation.id}/edit`),
          },
          {
            label: t("hse.actions.export_pdf"),
            icon: DocumentArrowDownIcon,
            onClick: () => handleExportDeviationPdf(drawerDeviation),
          },
          ...(drawerDeviation?.work_order?.id
            ? [
                {
                  label: t("deviation.work_order.label"),
                  icon: WrenchScrewdriverIcon,
                  onClick: () =>
                    navigate(
                      `/dashboard/work_orders/${drawerDeviation.work_order.id}`
                    ),
                },
              ]
            : []),
        ]}
      >
        {drawerDeviation && (
          <>
            <DrawerSection title={t("deviation.table.status")}>
              <div className="flex flex-wrap gap-2">
                <DeviationStatus deviation={drawerDeviation} />
                <DeviationSeverity severity={drawerDeviation?.severity} />
                <DeviationPriority priority={drawerDeviation?.priority} />
              </div>
            </DrawerSection>
            <DrawerSection>
              <DrawerProps
                items={[
                  {
                    label: t("deviation.list.type"),
                    value:
                      drawerDeviation.deviation_type?.displayTitle ||
                      drawerDeviation.deviation_type?.title ||
                      "—",
                  },
                  {
                    label: t("customer"),
                    value: drawerDeviation.customer?.displayTitle || "—",
                  },
                  {
                    label: t("project"),
                    value: drawerDeviation.project?.displayTitle || "—",
                  },
                  {
                    label: t("deviation.table.responsible"),
                    value:
                      drawerDeviation.responsible_user?.displayTitle || "—",
                  },
                  {
                    label: t("deviation.work_order.label"),
                    value:
                      drawerDeviation.work_order?.displayTitle ||
                      t("deviation.table.unlinked"),
                  },
                  {
                    label: t("deviation.table.created"),
                    value: drawerDeviation.created_at
                      ? dateToString(drawerDeviation.created_at)
                      : "—",
                  },
                ]}
              />
            </DrawerSection>
          </>
        )}
      </RightDrawer>

      <DeviationNewDialog
        open={deviationNewDialogOpen}
        onClose={() => setDeviationNewDialogOpen(false)}
        selected={null}
      />
    </>
  );
};
